import { ReactNode, useEffect, useState } from "react";
import { useStore } from "./user/UserContext";
import { useTheme } from "./Theme/ThemeContext";
import { Link, useNavigate } from "react-router-dom";
import * as utils from "../utils/utils";
import Shadowing from "./user/Shadowing";
import SideNav from "./SideNav/SideNav";
import AccountBanner from "./user/AccountBanner";
import Icon from "./Icon/Icon";
import NotificationPanel from "./NotificationsPanel/NotificationPanel";
import UserDropdown from "./DropDown/UserDropdown";
import { isLoggedIn } from "../utils/utils";

interface LayoutProps {
  children: ReactNode;
}

function PageLayout({ children }: LayoutProps) {
  const { store, setStore } = useStore();
  const { isDark } = useTheme();
  const uiSettings = store?.uiSettings;
  const user = utils.user(store);

  const [showSidebar, setShowSidebar] = useState<boolean>(uiSettings?.isSidebarOpen ?? true);
  const [unreadNotifications, setUnreadNotifications] = useState<any[]>([]);
  const [unreadCount, setUnreadCount] = useState<number>(0);
  const [isNotificationOpen, setIsNotificationOpen] = useState<boolean>(false);

  const navigate = useNavigate();
  // const location = useLocation();

  const shouldHideSidebar =
    [
      "suspended",
      "on_hold",
      "archived",
      "awaiting_review",
      "submitted",
      "under_review",
      "agreed",
      "awaiting_payment",
      "rejected"
    ].includes(user?.status) ||
    // Both statuses the API treats as blocked (BLOCKED_STATUSES): an owed
    // payment, and a trial that has run out. A lapsed trial used to keep the
    // whole dashboard open because only "on_hold" was checked here.
    (user?.role_id !== 5 && ["on_hold", "trial_expired"].includes(user?.account_status));

  // useEffect(() => {
  //   if (!isLoggedIn(store)) {
  //     navigate("/unauthorized", { state: { from: location.pathname } });
  //   }
  // }, [navigate, location, store]);

  useEffect(() => {
    if (shouldHideSidebar) {
      setShowSidebar(false);
    }
  }, [shouldHideSidebar]);

  useEffect(() => {
    function handleResize() {
      if (window.innerWidth <= 768) {
        setShowSidebar(false);
        const storeClone = { ...store };
        const uiSettings: any = storeClone.uiSettings ?? {};
        uiSettings.isSidebarOpen = false;
        storeClone.uiSettings = uiSettings;
        setStore(storeClone);
      }
    }

    handleResize();
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  useEffect(() => {
    if (isLoggedIn(store)) {
      getNotifications();
    }
  }, [store]);

  async function getNotifications() {
    const notifications = store?.notifications;
    setUnreadNotifications(notifications);
    setUnreadCount(notifications?.length);
  }

  function toggleSideBar() {
    const newValue = !showSidebar;
    setShowSidebar(newValue);
    const storeClone = { ...store };
    const uiSettings: any = storeClone.uiSettings ?? {};
    uiSettings.isSidebarOpen = newValue;
    storeClone.uiSettings = uiSettings;
    setStore(storeClone);
  }

  function toggleNotifications() {
    setIsNotificationOpen(!isNotificationOpen);
  }

  /* -------------------------------- */
  /* RENDER METHODS */
  /* -------------------------------- */

  function renderMainScreen() {
    return <div className="flex-1 overflow-y-auto bg-gray-100 p-4">{children}</div>;
  }

  function render() {
    return (
      <div>
        {isLoggedIn(store) ? (
          <div className="flex h-screen overflow-hidden">
            {!shouldHideSidebar && (
              <div
                className={`flex-shrink-0 transition-all duration-300 ease-in-out ${
                  showSidebar ? "w-64" : "w-20"
                }`}
              >
                <SideNav isSidebarOpen={showSidebar} />
              </div>
            )}
            <div className="flex flex-1 flex-col overflow-hidden">
              <div
                className={`px-5 py-3 shadow-sm transition-colors border-b-2 border-primary ${isDark ? "bg-primary-dark" : "bg-white"}`}
              >
                <div className="flex flex-row items-center justify-between">
                  {/* Left: sidebar toggle or logo */}
                  <div className="flex items-center gap-4">
                    {shouldHideSidebar && (
                      <a
                        className="flex cursor-pointer items-center gap-2"
                        onClick={() => navigate("/dashboard")}
                      >
                        <img
                          src={process.env.PUBLIC_URL + "/img/logos/logo-2.png"}
                          alt="Logo"
                          className="w-8"
                        />
                        <span
                          className={`text-lg font-bold ${isDark ? "text-white" : "text-gray-800"}`}
                        >
                          RentalEase
                        </span>
                      </a>
                    )}
                    {!shouldHideSidebar && (
                      <Icon
                        className={`cursor-pointer transition-all duration-300 ${
                          !showSidebar ? "rotate-180" : ""
                        } ${isDark ? "text-white opacity-70 hover:opacity-100" : "text-gray-500 hover:text-gray-800"}`}
                        name="fa-angles-left"
                        onClick={toggleSideBar}
                      />
                    )}
                  </div>

                  <AccountBanner />

                  {/* Right: plan pill, bell, user */}
                  <div className="flex items-center gap-5">
                    {store?.impersonatingUser && <Shadowing />}
                    {utils.isUserOnAgencyPlan(store) && (
                      <div
                        className={`hidden rounded-full border px-3 py-1 text-xs font-semibold md:block ${isDark ? "border-primary/40 bg-primary/20 text-primary" : "border-primary/30 bg-primary/10 text-primary-dark"}`}
                      >
                        {utils.getPlanName(store)}
                      </div>
                    )}
                    {/* Bell */}
                    <div className="relative">
                      <Link
                        onClick={toggleNotifications}
                        to="#"
                        className="flex items-center opacity-70 transition-opacity hover:opacity-100"
                      >
                        <Icon
                          name="fa-bell"
                          className={`text-lg ${isDark ? "text-white" : "text-gray-600"}`}
                        />
                        {unreadCount > 0 && (
                          <span className="absolute -right-2 -top-2 flex h-4 min-w-4 items-center justify-center rounded-full bg-red-500 px-1 text-[10px] font-bold leading-none text-white">
                            {unreadCount > 99 ? "99+" : unreadCount}
                          </span>
                        )}
                      </Link>
                    </div>

                    {/* Divider */}
                    <div className={`h-6 w-px ${isDark ? "bg-white/20" : "bg-gray-200"}`} />

                    <UserDropdown />
                  </div>
                </div>
              </div>

              {isNotificationOpen && (
                <NotificationPanel
                  notifications={unreadNotifications}
                  onClose={(notificationsFromPanel: any) => {
                    toggleNotifications();
                    setUnreadNotifications(notificationsFromPanel);
                    setUnreadCount(notificationsFromPanel?.length);
                  }}
                />
              )}

              {renderMainScreen()}
            </div>
          </div>
        ) : (
          renderMainScreen()
        )}
      </div>
    );
  }

  return render();
}

export default PageLayout;
